import React from "react";
import { Link } from "react-router-dom";

const UPDATED = "June 28, 2026";

function LegalShell({ title, children }) {
  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-16 min-h-[70vh]">
      <Link to="/" className="text-sm text-orange-600 hover:underline">← Back to Voyes</Link>
      <h1 className="font-display text-4xl font-semibold tracking-tight mt-4">{title}</h1>
      <p className="text-sm text-slate-400 mt-2">Last updated: {UPDATED}</p>
      <div className="prose prose-slate mt-8 space-y-6 text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl font-semibold text-slate-900 mb-2">{title}</h2>
      <div className="space-y-2 text-[15px]">{children}</div>
    </section>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy">
      <p>Voyes ("we", "us", or "our") operates the Voyes local discovery platform. This Privacy Policy explains how we collect, use, and protect your information when you use our website and services.</p>
      <Section title="1. Information We Collect">
        <p>• <strong>Account information</strong> — your name, email address, and password (stored securely as a hashed value) when you create an account.</p>
        <p>• <strong>Google sign-in</strong> — if you sign in with Google, we receive your name, email, and profile picture from Google.</p>
        <p>• <strong>Business information</strong> — details you provide when listing a business (name, description, address, contact details, photos).</p>
        <p>• <strong>Usage data</strong> — favorites, follows, and pages you view to personalize your experience.</p>
      </Section>
      <Section title="2. How We Use Your Information">
        <p>We use your information to provide and improve the service, authenticate you, display businesses you save or follow, and communicate important updates. We do not sell your personal information.</p>
      </Section>
      <Section title="3. Cookies">
        <p>We use essential cookies to keep you signed in (authentication session) and to remember your cookie preferences. You can control cookies through your browser settings; disabling essential cookies may affect functionality.</p>
      </Section>
      <Section title="4. Third-Party Services">
        <p>We use Google for optional social sign-in and OpenStreetMap to display business locations. These services have their own privacy policies governing the data they process.</p>
      </Section>
      <Section title="5. Data Security">
        <p>We take reasonable measures to protect your data, including password hashing and secure session cookies. No method of transmission over the internet is 100% secure.</p>
      </Section>
      <Section title="6. Your Rights">
        <p>You may access, update, or delete your account information at any time. To request deletion of your data, contact us at the email below.</p>
      </Section>
      <Section title="7. Children's Privacy">
        <p>Voyes is not directed to children under 13, and we do not knowingly collect data from them.</p>
      </Section>
      <Section title="8. Changes to This Policy">
        <p>We may update this policy from time to time. Continued use of Voyes after changes constitutes acceptance of the updated policy.</p>
      </Section>
      <Section title="9. Contact Us">
        <p>Questions about this Privacy Policy? Email us at <a href="mailto:Voyes.local@gmail.com" className="text-orange-600">Voyes.local@gmail.com</a>.</p>
      </Section>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service">
      <p>Welcome to Voyes. By accessing or using our website and services, you agree to be bound by these Terms of Service.</p>
      <Section title="1. Using Voyes">
        <p>You may browse Voyes as a visitor or create an account as a customer or business owner. You agree to provide accurate information and to keep your login credentials secure.</p>
      </Section>
      <Section title="2. Accounts">
        <p>You are responsible for all activity under your account. You must be at least 13 years old to create an account.</p>
      </Section>
      <Section title="3. Business Listings">
        <p>Business owners are responsible for the accuracy of their listings, deals, and events. Listings may be reviewed and require approval before appearing publicly. We may remove content that violates these terms or applicable law.</p>
      </Section>
      <Section title="4. Acceptable Use">
        <p>You agree not to post false, misleading, offensive, or unlawful content, infringe others' rights, or attempt to disrupt the platform. We may suspend accounts that violate these rules.</p>
      </Section>
      <Section title="5. Content & Intellectual Property">
        <p>You retain rights to content you submit but grant Voyes a license to display it on the platform. The Voyes name, logo, and design are our property.</p>
      </Section>
      <Section title="6. Deals & Events">
        <p>Voyes is a discovery platform. Deals and events are offered by independent businesses; we are not responsible for their fulfillment, pricing, or accuracy.</p>
      </Section>
      <Section title="7. Disclaimer & Limitation of Liability">
        <p>Voyes is provided "as is" without warranties of any kind. To the fullest extent permitted by law, Voyes is not liable for any indirect or consequential damages arising from use of the service.</p>
      </Section>
      <Section title="8. Termination">
        <p>We may suspend or terminate access to Voyes at any time for violations of these terms.</p>
      </Section>
      <Section title="9. Governing Law">
        <p>These terms are governed by the laws of the Province of Alberta, Canada.</p>
      </Section>
      <Section title="10. Contact">
        <p>Questions about these Terms? Email <a href="mailto:Voyes.local@gmail.com" className="text-orange-600">Voyes.local@gmail.com</a>.</p>
      </Section>
    </LegalShell>
  );
}